/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { call, delay, put, race } from "redux-saga/effects";

import { isEmpty } from "lodash";

import * as userProfileApi from "Services/UserProfileApi";

import { GENERIC_API_TIMEOUT_ERROR } from "Constants/ApplicationConstants";
import { HttpStatusCode } from "Constants/HttpStatusCode";
import {
  defaultListingPageSize,
  APPLICATION_STATUS,
  AVAILABLE_CPM_LIST,
  PROGRAM_STATUS,
} from "Constants/ProfileConstants";
import { IClaimBenefitDataResult } from "Interfaces/Benefits/IClaimBenefitDataResult";
import { IClaimBenefitResult } from "Interfaces/Benefits/IClaimBenefitResult";
import { IProfileBenefitDataResult } from "Interfaces/Benefits/IProfileBenefitDataResult";
import { IProfileBenefitResult } from "Interfaces/Benefits/IProfileBenefitResult";
import { IApiEmptyResult, IApiErrorResult, IApiResult2 } from "Interfaces/IApiResult";
import { IProgramProfile } from "Interfaces/ProgramProfile/IProgramProfile";
import { IDistributionListsResult } from "Interfaces/UserProfile/DistributionLists/IDistributionListsResult";
import { INewUserProfileCommentRequest } from "Interfaces/UserProfile/INewUserProfileCommentRequest";
import { IUserProfileBlockShippingDataResult } from "Interfaces/UserProfile/IUserProfileBlockShippingDataResult";
import { IUserProfileBlockShippingRequest } from "Interfaces/UserProfile/IUserProfileBlockShippingRequest";
import { IUserProfileCommentDataResult } from "Interfaces/UserProfile/IUserProfileCommentDataResult";
import { IUserProfileCommentRequest } from "Interfaces/UserProfile/IUserProfileCommentRequest";
import { IUserProfileCommentsDataResult } from "Interfaces/UserProfile/IUserProfileCommentsDataResult";
import { IUserProfileCommentsRequest } from "Interfaces/UserProfile/IUserProfileCommentsRequest";
import { IUserProfileDataResult } from "Interfaces/UserProfile/IUserProfileDataResult";
import { IUserProfileRequest } from "Interfaces/UserProfile/IUserProfileRequest";
import { IUserProfilesSearchDataResult } from "Interfaces/UserProfile/IUserProfilesSearchDataResult";
import { IUserProfilesSearchRequest } from "Interfaces/UserProfile/IUserProfilesSearchRequest";
import { IUserProfilesSearchResult } from "Interfaces/UserProfile/IUserProfilesSearchResult";
import { IUserProfileVSBenefit } from "Interfaces/UserProfile/IUserProfileVSBenefit";
import { IUserProfileVSBenefitsDataResult } from "Interfaces/UserProfile/IUserProfileVSBenefitsDataResult";
import { IUserProfileVSBenefitsUpdateDataResult } from "Interfaces/UserProfile/IUserProfileVSBenefitsUpdateDataResult";
import { IUserProfileVSBenefitsUpdateRequest } from "Interfaces/UserProfile/IUserProfileVSBenefitsUpdateRequest";
import { addDateTimeOffset } from "Utility/FormatHelper";
import { getDefaultApiTimeOutConfiguration } from "Utility/Utils";

import { getSchoolData } from "../School/SchoolDataAction";
import {
  getStateProvince,
  getStateProvinceSchool,
  getStateProvinceShipping,
} from "../StateProvince/StateProvinceAction";
import {
  addNewCommentFail,
  addNewCommentSuccess,
  claimBenefitFail,
  claimBenefitSuccess,
  endUserProfileVSBenefitFail,
  endUserProfileVSBenefitSuccess,
  extendUserProfileVSBenefitFail,
  extendUserProfileVSBenefitSuccess,
  fetchProgramProfileFail,
  fetchProgramProfileSuccess,
  getProfileBenefitsSuccess,
  loadUserProfileCommentsFail,
  loadUserProfileCommentsSuccess,
  loadUserProfileFail,
  loadUserProfileSuccess,
  loadUserProfileVSBenefitFail,
  loadUserProfileVSBenefitSuccess,
  profileBenefitsFail,
  provisionUserProfileVSBenefitFail,
  provisionUserProfileVSBenefitSuccess,
  searchUserProfilesFail,
  searchUserProfilesSuccess,
  updateProgramProfileFailure,
  updateProgramProfileSuccess,
  updateUserProfileFail,
  updateUserProfileSuccess,
  userProfileDlLoadFail,
  userProfileDlLoadSuccess,
  userProfileDlRemoveAllFail,
  userProfileDlRemoveAllSuccess,
  userProfileDlRemoveFail,
  userProfileDlRemoveSuccess,
  userProfileIsPrivateUpdateFail,
  userProfileIsPrivateUpdateSuccess,
  userProfileUpdateBlockShippingFail,
  userProfileUpdateBlockShippingSuccess,
  IAddNewComment,
  IClaimBenefit,
  IEndUserProfileVSBenefits,
  IExtendUserProfileVSBenefits,
  IFetchProgramProfile,
  IGetProfileBenefits,
  ILoadUserProfile,
  ILoadUserProfileByEmail,
  ILoadUserProfileComments,
  ILoadUserProfileVSBenefits,
  IProvisionUserProfileVSBenefits,
  ISearchUserProfiles,
  IUpdateProgramProfile,
  IUpdateUserProfile,
  IUpdateUserProfileComment,
  IUserProfileDlLoad,
  IUserProfileDlRemove,
  IUserProfileDlRemoveAll,
  IUserProfileIsPrivateUpdate,
  IUserProfileUpdateBlockShipping,
} from "./UserProfileAction";
import { BenefitNameEnum } from "Constants/BenefitConstants";

export interface IProgramDetailsResult {
  programProfile: IProgramProfile;
}

export interface IProgramDetailsRequest {
  programProfile: IProgramProfile;
}

export type IProgramDetailsDataResult = IProgramDetailsResult | IApiErrorResult;

/**
 * Generator function to async dispatch action
 * @generator
 * @param {ISearchUserProfiles} action To search user profile for given text.
 * @returns {Generator}
 */
function* handleSearchUserProfiles(action: ISearchUserProfiles): Generator {
  const failedMessage = "Failed to search the user profiles.";

  const requestBody = {
    SearchKey: action.searchText,
    PageIndex: action.searchPage,
    PageSize: action.pageSize,
    applicationStatusList: [],
    programStatusList: [],
    milestoneList: [],
    benefitsProvisionedList: [],
    cpm: [],
    countryRegionList: [],
    stateProvinceList: [],
    otherProgramMembershipList: [],
    programStartDate: undefined,
    programEndDate: undefined,
  } as unknown as IUserProfilesSearchRequest;

  action.selectedUserProfilesFilterValues.forEach(function (item) {
    switch (item.fieldName) {
      case APPLICATION_STATUS:
        requestBody.applicationStatusList.push(item.value);
        break;
      case "milestone":
        requestBody.milestoneList.push(item.value);
        break;
      case PROGRAM_STATUS:
        requestBody.programStatusList.push(item.value);
        break;
      case "provisioned":
        requestBody.benefitsProvisionedList.push(item.value);
        break;
      case AVAILABLE_CPM_LIST:
        requestBody.cpm.push(item.value);
        break;
      case "country":
        requestBody.countryRegionList.push(item.value);
        break;
      case "regionState":
        requestBody.stateProvinceList.push(item.value);
        break;
      case "otherProgramMembership":
        requestBody.otherProgramMembershipList.push(item.value);
        break;
      case "programStartDate":
        requestBody.programStartDate = new Date(item.value);
        break;
      case "programEndDate":
        requestBody.programEndDate = new Date(item.value);
        break;
      default:
        return;
    }
  });

  try {
    const { result, timeout }: IApiResult2<IUserProfilesSearchDataResult> | any = yield race({
      result: call(userProfileApi.searchUserProfiles, requestBody),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(searchUserProfilesFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const { userProfiles, filteredCount } = result.parsedBody as IUserProfilesSearchResult;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        // TODO: if null result, response code is 200 and "userProfile" returned.
        // If backend updated to instead return "userProfiles" we can clean this up some
        if (isEmpty(userProfiles)) {
          yield put(searchUserProfilesSuccess([], 1, defaultListingPageSize, 0));
        } else {
          yield put(
            searchUserProfilesSuccess(
              userProfiles,
              action.searchPage,
              action.pageSize,
              filteredCount
            )
          );
        }
        break;
      case HttpStatusCode.BadRequest:
        yield put(searchUserProfilesFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(searchUserProfilesFail(failedMessage));
        break;
    }
  } catch (error) {
    // TODO: getting bad request and error returned when no users found and falling into this catch
    yield put(searchUserProfilesFail(error));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {ILoadUserProfile} action To load user profile by id.
 * @returns {Generator}
 */
function* handleLoadUserProfile(action: ILoadUserProfile): Generator {
  const failedMessage = "Failed to load the user profile.";
  const { userProfileId } = action;

  try {
    const { result, timeout }: IApiResult2<IUserProfileDataResult> | any = yield race({
      result: call(userProfileApi.getUserProfile, userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(loadUserProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const userProfile = (result.parsedBody as IUserProfileRequest).userProfile;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(loadUserProfileSuccess(userProfile));
        if (!!userProfile.addressCountryOrRegionName) {
          yield put(getStateProvince(userProfile.addressCountryOrRegionName));
        }

        if (!!userProfile.shippingCountryOrRegionName) {
          yield put(getStateProvinceShipping(userProfile.shippingCountryOrRegionName));
        }

        // TODO: hard coded index since MSP can only have 1 school.
        if (!!userProfile.userProfileSchool && userProfile.userProfileSchool.length > 0) {
          if (!!userProfile.userProfileSchool[0]?.schoolCountryRegionName) {
            yield put(
              getStateProvinceSchool(userProfile.userProfileSchool[0].schoolCountryRegionName)
            );
            yield put(getSchoolData(userProfile.userProfileSchool[0].schoolCountryRegionName));
          }
        }
        break;
      case HttpStatusCode.BadRequest:
        yield put(loadUserProfileFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(loadUserProfileFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(loadUserProfileFail(error));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {ILoadUserProfileByEmail} action To load user profile by email.
 * @returns {Generator}
 */
function* handleLoadUserProfileByEmail(action: ILoadUserProfileByEmail): Generator {
  const failedMessage = "Failed to load the user profile.";
  const { email } = action;

  try {
    const { result, timeout }: IApiResult2<IUserProfileDataResult> | any = yield race({
      result: call(userProfileApi.getUserProfileByEmail, email),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(loadUserProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const userProfile = (result.parsedBody as IUserProfileRequest).userProfile;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(loadUserProfileSuccess(userProfile));
        if (!!userProfile.addressCountryOrRegionName) {
          yield put(getStateProvince(userProfile.addressCountryOrRegionName));
        }

        if (!!userProfile.shippingCountryOrRegionName) {
          yield put(getStateProvinceShipping(userProfile.shippingCountryOrRegionName));
        }

        // TODO: hard coded index since MSP can only have 1 school.
        if (!!userProfile.userProfileSchool && userProfile.userProfileSchool.length > 0) {
          if (!!userProfile.userProfileSchool[0]?.schoolCountryRegionName) {
            yield put(
              getStateProvinceSchool(userProfile.userProfileSchool[0].schoolCountryRegionName)
            );
            yield put(getSchoolData(userProfile.userProfileSchool[0].schoolCountryRegionName));
          }
        }
        break;
      case HttpStatusCode.BadRequest:
        yield put(loadUserProfileFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(loadUserProfileFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(loadUserProfileFail(error));
  }
}

/**
 * Handle calling the api to update a user profile asynchronously.
 * @param {IUpdateUserProfile} action To update user profile.
 * @returns {Generator} the profile actions.
 */
function* handleUpdateUserProfile(action: IUpdateUserProfile): Generator {
  const failedMessage = "Failed to update the user profile";
  const userProfileToUpdate: IUserProfileRequest = {
    userProfile: {
      ...action.userProfile,
      dateOfBirth: addDateTimeOffset(action.userProfile.dateOfBirth),
    },
  };
  try {
    const { result, timeout }: IApiResult2<IUserProfileDataResult> | any = yield race({
      result: call(userProfileApi.updateUserProfile, userProfileToUpdate),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateUserProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(updateUserProfileSuccess((result.parsedBody as IUserProfileRequest).userProfile));
        break;
      case HttpStatusCode.BadRequest:
        yield put(updateUserProfileFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(updateUserProfileFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(updateUserProfileFail(error.toString()));
  }
}

/**
 * Handle calling the api to update a user profile asynchronously.
 * @param {IUpdateUserProfile} action To update user profile.
 * @returns {Generator} the profile actions.
 */
function* handleUpdateUserProfileBlockShipping(action: IUserProfileUpdateBlockShipping): Generator {
  const failedMessage = "Failed to update the shipping status for this user profile.";

  const userProfileToUpdate: IUserProfileBlockShippingRequest = {
    userProfileId: action.userProfileId,
    blockShipping: action.isShippingBlocked,
  };

  try {
    const { result, timeout }: IApiResult2<IUserProfileBlockShippingDataResult> | any = yield race({
      result: call(userProfileApi.updateUserProfileBlockShipping, userProfileToUpdate),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(userProfileUpdateBlockShippingFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(userProfileUpdateBlockShippingSuccess(result.parsedBody as number));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          userProfileUpdateBlockShippingFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(userProfileUpdateBlockShippingFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(userProfileUpdateBlockShippingFail(error.toString()));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {ILoadUserProfileComments} action To load user profile comments.
 * @returns {Generator}
 */
function* handleLoadUserProfileComments(action: ILoadUserProfileComments): Generator {
  const failedMessage = "Failed to load the user's profile comments.";
  const { userProfileId } = action;

  try {
    const { result, timeout }: IApiResult2<IUserProfileCommentsDataResult> | any = yield race({
      result: call(userProfileApi.getUserProfileComments, userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(loadUserProfileCommentsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const userProfileComments =
      (result.parsedBody as IUserProfileCommentsRequest)?.userProfileComments || [];

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(loadUserProfileCommentsSuccess(userProfileComments));
        break;
      case HttpStatusCode.NoContent:
        yield put(loadUserProfileCommentsSuccess([]));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          loadUserProfileCommentsFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(loadUserProfileCommentsFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(loadUserProfileCommentsFail(error));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IUpdateUserProfileComment} action To update user profile comment.
 * @returns {Generator}
 */
function* handleUpdateUserProfileComment(action: IUpdateUserProfileComment): Generator {
  const failedMessage = "Failed to update the user profile comment";
  const userProfileCommentToUpdate: IUserProfileCommentRequest = {
    userProfileComment: action.userProfileComment,
  };
  const userProfileId = action.userProfileComment.userProfileId;

  try {
    const { result, timeout }: IApiResult2<IUserProfileDataResult> | any = yield race({
      result: call(
        userProfileApi.updateUserProfileComment,
        userProfileId,
        userProfileCommentToUpdate
      ),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateUserProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(updateUserProfileSuccess((result.parsedBody as IUserProfileRequest).userProfile));
        break;
      case HttpStatusCode.BadRequest:
        yield put(updateUserProfileFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(updateUserProfileFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(updateUserProfileFail(error));
  }
}

/**
 * Generator function to async dispatch action
 * TODO: This function gets VsBenefits (as IUserProfileVSBenefit) rather than UserProfileVSBenefits.
 * TODO: This will need to be changed if we need to get more types of benefits than VsBenefits.
 * @generator
 * @param {ILoadUserProfileVSBenefits} action To load user profile benefits.
 * @returns {Generator}
 */
function* handleLoadUserProfileVSBenefits(action: ILoadUserProfileVSBenefits): Generator {
  const failedMessage = "Failed to load the user's profile benefits.";
  const { userProfileId } = action;

  try {
    const { result, timeout }: IApiResult2<IUserProfileVSBenefitsDataResult> | any = yield race({
      result: call(userProfileApi.getUserProfileVSBenefits, userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(loadUserProfileVSBenefitFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const userProfileVSBenefit = result.parsedBody as IUserProfileVSBenefit;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(loadUserProfileVSBenefitSuccess(userProfileVSBenefit));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          loadUserProfileVSBenefitFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(loadUserProfileVSBenefitFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(loadUserProfileVSBenefitFail(error));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IProvisionUserProfileVSBenefits} action To provision a user profile visual studio benefits.
 * @returns {Generator}
 */
function* handleProvisionUserProfileVSBenefits(action: IProvisionUserProfileVSBenefits): Generator {
  const failedMessage = "Failed to provision the user's profile benefits.";
  const { userProfileId } = action;

  try {
    const { result, timeout }: IApiResult2<IUserProfileVSBenefitsUpdateDataResult> | any =
      yield race({
        result: call(userProfileApi.provisionUserProfileVSBenefits, userProfileId),
        timeout: delay(getDefaultApiTimeOutConfiguration()),
      });

    if (timeout) {
      yield put(provisionUserProfileVSBenefitFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const userProfileVSBenefit = result.parsedBody as IUserProfileVSBenefitsUpdateRequest;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(provisionUserProfileVSBenefitSuccess(userProfileVSBenefit));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          provisionUserProfileVSBenefitFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(provisionUserProfileVSBenefitFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(provisionUserProfileVSBenefitFail(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IExtendUserProfileVSBenefits} action To extend a user profile visual studio benefits.
 * @returns {Generator}
 */
function* handleExtendUserProfileVSBenefits(action: IExtendUserProfileVSBenefits): Generator {
  const failedMessage = "Failed to extend the user's profile benefits.";
  const { userProfileId } = action;

  try {
    const { result, timeout }: IApiResult2<IUserProfileVSBenefitsUpdateDataResult> | any =
      yield race({
        result: call(userProfileApi.extendUserProfileVSBenefits, userProfileId),
        timeout: delay(getDefaultApiTimeOutConfiguration()),
      });

    if (timeout) {
      yield put(extendUserProfileVSBenefitFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const userProfileVSBenefit = result.parsedBody as IUserProfileVSBenefitsUpdateRequest;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(extendUserProfileVSBenefitSuccess(userProfileVSBenefit));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          extendUserProfileVSBenefitFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(extendUserProfileVSBenefitFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(extendUserProfileVSBenefitFail(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IEndUserProfileVSBenefits} action To end a user profile visual studio benefits.
 * @returns {Generator}
 */
function* handleEndUserProfileVSBenefits(action: IEndUserProfileVSBenefits): Generator {
  const failedMessage = "Failed to end the user's profile benefits.";
  const { userProfileId } = action;

  try {
    const { result, timeout }: IApiResult2<IUserProfileVSBenefitsUpdateDataResult> | any =
      yield race({
        result: call(userProfileApi.endUserProfileVSBenefits, userProfileId),
        timeout: delay(getDefaultApiTimeOutConfiguration()),
      });

    if (timeout) {
      yield put(endUserProfileVSBenefitFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const userProfileVSBenefit = result.parsedBody as IUserProfileVSBenefitsUpdateRequest;

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(endUserProfileVSBenefitSuccess(userProfileVSBenefit));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          endUserProfileVSBenefitFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(endUserProfileVSBenefitFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(endUserProfileVSBenefitFail(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IUpdateProgramProfile} action To update program profile.
 * @returns {Generator}
 */
function* handleUpdateProgramProfile(action: IUpdateProgramProfile): Generator {
  const failedMessage = "Failed to update Program Details.";

  const programDetailsRequest: IProgramDetailsRequest = {
    programProfile: action.programProfile,
  };

  try {
    const { result, timeout }: IApiResult2<IProgramDetailsDataResult> | any = yield race({
      result: call(userProfileApi.updateProgramProfile, programDetailsRequest),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(updateProgramProfileFailure(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        const programProfile = (result.parsedBody as IProgramDetailsResult).programProfile;
        yield put(updateProgramProfileSuccess(programProfile));
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          updateProgramProfileFailure((result.parsedBody as IApiErrorResult).error.message)
        );
      default:
        yield put(
          updateProgramProfileFailure((result.parsedBody as IApiErrorResult).error.message)
        );
    }
  } catch (error) {
    yield put(updateProgramProfileFailure(failedMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IUpdateProgramProfile} action To claim program profile benefit.
 * @returns {void}
 */
function* handleClaimBenefit(action: IClaimBenefit) {
  const failedMessage =
    "API call failed, please try again and let us know if issue is not resolved.";

  //TODO: no MLSA benefits yet. Revist when we have them.
  const benefitApiMap = {
    [BenefitNameEnum.SwagKit]: userProfileApi.claimUserProfileBenefitSwagkit,
  };

  try {
    const apiCall = benefitApiMap[action.benefitType];

    const { result, timeout }: IApiResult2<IClaimBenefitDataResult> | any = yield race({
      result: call(apiCall, action.userProfileId, action.tenant),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(claimBenefitFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.CreatedAt:
        const requestResult = result.parsedBody as IClaimBenefitResult;
        if (!requestResult.isSuccess) {
          yield put(claimBenefitFail(failedMessage));
        }
        yield put(claimBenefitSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(claimBenefitFail((result.parsedBody as IApiErrorResult).error.message));
      default:
        yield put(claimBenefitFail((result.parsedBody as IApiErrorResult).error.message));
    }
  } catch (error) {
    yield put(claimBenefitFail(failedMessage));
  }
}

function* handleGetProfileBenefits(action: IGetProfileBenefits): Generator {
  const defaultFailedErrorMessage = "Failed to get profile benefits.";

  try {
    const { result, timeout }: IApiResult2<IProfileBenefitDataResult> | any = yield race({
      result: call(userProfileApi.getProfileBenefits, action.userProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(profileBenefitsFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.httpStatusCode) {
      case HttpStatusCode.Ok:
        yield put(getProfileBenefitsSuccess(result.parsedBody as IProfileBenefitResult));
        break;
      case HttpStatusCode.BadRequest:
        yield put(profileBenefitsFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(profileBenefitsFail(defaultFailedErrorMessage));
    }
  } catch (error) {
    yield put(profileBenefitsFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IMVPFetchProgramProfile} action To get Program profile.
 * @returns {void}
 */
function* handleProgramProfileFetch(action: IFetchProgramProfile) {
  const defaultFailedErrorMessage = "Failed to fetch Program profile";
  const { programProfileId } = action;
  try {
    const { result, timeout } = yield race({
      result: call(userProfileApi.fetchProgramProfile, programProfileId),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(fetchProgramProfileFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const programProfile = result.parsedBody as IProgramProfile;

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(fetchProgramProfileSuccess(programProfile));
        break;
      case HttpStatusCode.BadRequest:
        yield put(fetchProgramProfileFail((result.body as IApiErrorResult).error.message));
        break;
      default:
        yield put(fetchProgramProfileFail(defaultFailedErrorMessage));
        break;
    }
  } catch (error) {
    yield put(fetchProgramProfileFail(defaultFailedErrorMessage));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IAddNewComment} action To add comment.
 * @returns {void}
 */
function* handleAddComment(action: IAddNewComment) {
  const failedMessage = "Failed to create the user profile comment";
  const userProfileCommentToCreate: INewUserProfileCommentRequest = {
    userProfileComment: {
      userProfileId: action.userProfileId,
      text: action.newComment,
      createdBy: action.createdBy,
    },
  };

  try {
    const { result, timeout }: IApiResult2<IUserProfileCommentDataResult> | any = yield race({
      result: call(
        userProfileApi.createUserProfileComment,
        action.userProfileId,
        userProfileCommentToCreate
      ),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(addNewCommentFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(addNewCommentSuccess(action.userProfileId));
        break;
      case HttpStatusCode.BadRequest:
        yield put(addNewCommentFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(addNewCommentFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(addNewCommentFail(error));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IUserProfileDlLoad} action To load dl.
 * @returns {void}
 */
function* handleDlLoad(action: IUserProfileDlLoad) {
  const failedMessage = "Failed to create to load dl for profile";

  try {
    const { result, timeout }: IApiResult2<IUserProfileCommentDataResult> | any = yield race({
      result: call(userProfileApi.loadDistributionLists, action.userProfileId, action.tenant),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(userProfileDlLoadFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    const distributionLists = result.parsedBody as IDistributionListsResult;

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(userProfileDlLoadSuccess(distributionLists.details));
        break;
      case HttpStatusCode.BadRequest:
        yield put(userProfileDlLoadFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(userProfileDlLoadFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(userProfileDlLoadFail(error));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IUserProfileDlLoad} action To remove dl.
 * @returns {void}
 */
function* handleDlRemove(action: IUserProfileDlRemove) {
  const failedMessage = "Failed to remove the distribution list for the profile.";
  try {
    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(
        userProfileApi.removeDistributionLists,
        action.dlGroup,
        action.userEmail,
        action.tenant
      ),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });
    if (timeout) {
      yield put(userProfileDlRemoveFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }
    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(userProfileDlRemoveSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(userProfileDlRemoveFail((result.parsedBody as IApiErrorResult)?.error?.message));
        break;
      default:
        yield put(userProfileDlRemoveFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(userProfileDlRemoveFail(error));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IUserProfileDlLoad} action To remove dl.
 * @returns {void}
 */
function* handleDlRemoveAll(action: IUserProfileDlRemoveAll) {
  const failedMessage = "Failed to remove all dl for profile";

  try {
    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(userProfileApi.removeAllDistributionLists, action.userProfileId, action.tenant),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(userProfileDlRemoveAllFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(userProfileDlRemoveAllSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(userProfileDlRemoveAllFail((result.parsedBody as IApiErrorResult).error.message));
        break;
      default:
        yield put(userProfileDlRemoveAllFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(userProfileDlRemoveAllFail(error));
  }
}

/**
 * Generator function to async dispatch action
 * @generator
 * @param {IUserProfileDlLoad} action To remove dl.
 * @returns {void}
 */
function* handleUserProfileIsPrivateUpdate(action: IUserProfileIsPrivateUpdate) {
  const failedMessage = "Failed to set the is private flag for profile";

  try {
    const { result, timeout }: IApiResult2<IApiEmptyResult> | any = yield race({
      result: call(
        userProfileApi.updateUserProfileIsPrivate,
        action.userProfileId,
        action.isPrivate
      ),
      timeout: delay(getDefaultApiTimeOutConfiguration()),
    });

    if (timeout) {
      yield put(userProfileIsPrivateUpdateFail(GENERIC_API_TIMEOUT_ERROR));
      return;
    }

    switch (result.status) {
      case HttpStatusCode.Ok:
        yield put(userProfileIsPrivateUpdateSuccess());
        break;
      case HttpStatusCode.BadRequest:
        yield put(
          userProfileIsPrivateUpdateFail((result.parsedBody as IApiErrorResult).error.message)
        );
        break;
      default:
        yield put(userProfileIsPrivateUpdateFail(failedMessage));
        break;
    }
  } catch (error) {
    yield put(userProfileIsPrivateUpdateFail(error));
  }
}

export {
  handleSearchUserProfiles,
  handleLoadUserProfile,
  handleLoadUserProfileByEmail,
  handleUpdateUserProfile,
  handleUpdateUserProfileBlockShipping,
  handleLoadUserProfileComments,
  handleUpdateUserProfileComment,
  handleLoadUserProfileVSBenefits,
  handleProvisionUserProfileVSBenefits,
  handleExtendUserProfileVSBenefits,
  handleEndUserProfileVSBenefits,
  handleUpdateProgramProfile,
  handleClaimBenefit,
  handleGetProfileBenefits,
  handleProgramProfileFetch,
  handleAddComment,
  handleDlLoad,
  handleDlRemove,
  handleDlRemoveAll,
  handleUserProfileIsPrivateUpdate,
};
